/**
 * `#intelHeadlines` while one unit is focused (`#intelFocus`, or a map click) — client
 * round 3 Oct 2026, the UC2 half of `Uc1FocusTiles.tsx` and for its reason: four
 * programme figures under one watershed's name answer a question the reader has just
 * stopped asking.
 *
 * The four tiles are the focused unit's own: its class, its cropland change, its rainfall
 * tendency in words, and its adoption of whichever group `#uc2Practice` is on — so the
 * practice selector moves this block too. Every one of them is read off the units row the
 * choropleth is already painted from, with the detail standing in for a country row
 * (whose numbers live there and not on the row), so the block costs no request.
 *
 * The way back is the same small "Programme" link UC1 carries.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUnitDetail, IntelUnitRow } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { StatTile, StatTiles } from './charts/StatTile';

export interface Uc2FocusTilesProps {
  /** The focused unit's own row, or `undefined` while the rows have not landed. */
  row: IntelUnitRow | undefined;
  /** `uc2/units/{id}` — the fallback for anything the row does not carry. */
  detail: IntelUnitDetail | null;
  name: string;
  /** `#uc2Practice` — which group the adoption tile reads. */
  practice: string;
  onBack: () => void;
}

/** The first of the two that carries a real number. */
function numberOf(...values: (number | null | undefined)[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

export function Uc2FocusTiles({ row, detail, name, practice, onBack }: Uc2FocusTilesProps) {
  const { t } = useTranslation();
  const category = row?.category ?? detail?.category ?? null;
  const cropland = numberOf(row?.cropland_delta_pct, detail?.cropland_delta_pct);
  const tendency = row?.rain_tendency ?? detail?.rain_tendency ?? null;
  const adoption = numberOf(row?.adoption?.[practice], detail?.adoption?.[practice]);

  return (
    <div id="intelHeadlines" className="intel-headlines intel-card" data-testid="uc2-focus-tiles">
      <h5 className="intel-card-title">{name}</h5>
      <StatTiles>
        <StatTile
          id="uc2-focus-class"
          value={category ? t(`intel.uc2.category.${category}`, { defaultValue: category }) : '-'}
          text
          label={t('intel.uc2.tile.unitClass')}
        />
        <StatTile
          id="uc2-focus-cropland"
          value={cropland != null ? fmtNum(cropland, 1) : '-'}
          unit={cropland != null ? '%' : undefined}
          label={t('intel.uc2.tile.unitCropland')}
        />
        <StatTile
          id="uc2-focus-rain"
          value={tendency ? t(`intel.uc2.legend.rain.${tendency}`, { defaultValue: tendency }) : '-'}
          text
          label={t('intel.uc2.tile.unitRain')}
        />
        <StatTile
          id="uc2-focus-adoption"
          value={adoption != null ? fmtNum(adoption, 0) : '-'}
          unit={adoption != null ? '%' : undefined}
          label={t('intel.uc2.tile.unitAdoption', { name: t(`intel.uc2.practice.${practice}`) })}
        />
      </StatTiles>
      <button type="button" className="intel-focusback" data-testid="uc2-focus-back" onClick={onBack}>
        {t('intel.focusProgramme')}
      </button>
    </div>
  );
}

export default Uc2FocusTiles;
